import { ReactElement, RefObject, useState } from 'react';
import ValidableControl from 'components/shared/ValidableControl';
import { State } from 'models/Address';
import { Component as CaretDown } from 'icons/icon-ui--caret-outline.svg';
import { MAX_ADDRESS2_LENGTH } from '../../../utils/config';
import styles from './AddressForm.module.scss';

export type FieldName =
  | 'base'
  | 'address'
  | 'address2'
  | 'zip'
  | 'state'
  | 'city'

export type AddressFieldsValue = {
  address1: string;
  address2: string;
  zip: string;
  state: string;
  city: string;
};

/**
 * Props:
 * values: current form values for all address fields
 * onChange: callback that applies a partial update to the given form values
 * getErrorMsg / isInvalid: map of input to error / validity
 * refs: input refs used to scroll to errors
 * className: optional class name
 */
type Props = {
  values: AddressFieldsValue;
  onChange: (update: Partial<AddressFieldsValue>) => void;
  getErrorMsg: (attr: FieldName) => string;
  isInvalid: (attr: FieldName) => boolean;
  refs: {
    baseRef: RefObject<HTMLDivElement>;
    addressInputRef: RefObject<HTMLDivElement>;
    address2InputRef: RefObject<HTMLDivElement>;
    zipRef: RefObject<HTMLDivElement>;
    cityRef: RefObject<HTMLDivElement>;
    stateRef: RefObject<HTMLDivElement>;
  };
  className?: string;
};

// Reusable Address Form
export function BillingAddressForm({
  values,
  onChange,
  getErrorMsg,
  isInvalid,
  refs,
  className = '',
}: Props): ReactElement {
  const [showAddress2, setShowAddress2] = useState(false);

  return (
    <div className={[styles.address_form, className].join(' ')}>
      {isInvalid('base') && (
        <div className="mb-4 col-12 u-text--sm">
          <div className="c-alert c-alert--error" role="alert">
            <div className="c-alert__text" ref={refs.baseRef ?? null}>
              {getErrorMsg('base')}
            </div>
          </div>
        </div>
      )}

      <div className={styles.input_field} ref={refs.addressInputRef}>
        <ValidableControl
          label="Street Address *"
          htmlFor="add-address-address"
          isInvalid={isInvalid('address')}
          errorMessage={getErrorMsg('address')}
        >
          <input
            id="add-address-address"
            type="text"
            value={values.address1}
            onChange={(e) => onChange({ address1: e.target.value })}
          />
        </ValidableControl>
      </div>

      <div className={styles.input_field} ref={refs.address2InputRef}>
        {showAddress2 ? (
          <ValidableControl
            label={(
              <>
                Apartment/Suite&nbsp;
                <span>(optional)</span>
              </>
            )}
            htmlFor="add-address-address2"
            isInvalid={isInvalid('address2')}
            invalidClassName={values.address2.length > MAX_ADDRESS2_LENGTH
              ? 'is-invalid'
              : 'has-warning'}
            errorMessage={getErrorMsg('address2')}
          >
            <input
              id="add-address-address2"
              type="text"
              value={values.address2}
              onChange={(e) => {
                const next = e.target.value;

                if (next.length <= MAX_ADDRESS2_LENGTH) {
                  onChange({ address2: next });
                  return;
                }

                // allow backspace/delete (shrinking), block growth past limit
                if (next.length < values.address2.length) {
                  onChange({ address2: next });
                }
              }}
            />
          </ValidableControl>
        ) : (
          <div className="c-form-group">
            <button
              type="button"
              aria-label="Expand Apartment / Suite field"
              className="c-link u-text--sm"
              onClick={() => setShowAddress2(true)}
            >
              + Add Apartment / Suite (optional)
            </button>
          </div>
        )}
      </div>

      <div className={styles.input_field} ref={refs.zipRef}>
        <ValidableControl
          label="Zipcode *"
          htmlFor="add-address-zip"
          isInvalid={isInvalid('zip')}
          errorMessage={getErrorMsg('zip')}
        >
          <input
            id="add-address-zip"
            type="text"
            pattern="\d*"
            inputMode="numeric"
            maxLength={5}
            value={values.zip}
            onChange={(e) => onChange({ zip: e.target.value })}
          />
        </ValidableControl>
      </div>

      <div className={styles.input_field} ref={refs.cityRef}>
        <ValidableControl
          label="City *"
          htmlFor="add-address-city"
          isInvalid={isInvalid('city')}
          errorMessage={getErrorMsg('city')}
        >
          <input
            id="add-address-city"
            type="text"
            value={values.city}
            onChange={(e) => onChange({ city: e.target.value })}
          />
        </ValidableControl>
      </div>

      <div className={styles.input_field} ref={refs.stateRef}>
        <ValidableControl
          label="State *"
          htmlFor="add-address-state"
          isInvalid={isInvalid('state')}
          errorMessage={getErrorMsg('state')}
        >
          <div className={styles.select_wrap}>
            <select
              id="add-address-state"
              value={values.state}
              onChange={(e) => onChange({ state: e.target.value })}
            >
              <option value="">Select State</option>
              {(Object.keys(State) as Array<keyof typeof State>).map((code) => (
                <option key={code} value={code}>
                  {State[code]}
                </option>
              ))}
            </select>
            <span className={styles.select_caret} aria-hidden="true">
              <CaretDown width="10" />
            </span>
          </div>
        </ValidableControl>
      </div>
    </div>
  );
}
